import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ProgressTimerProps {
  duration?: number;
  showProgress?: boolean;
  onComplete?: () => void;
  resetKey?: string | number;
}

const ProgressTimer: React.FC<ProgressTimerProps> = ({
  duration = 2000,
  showProgress = false,
  onComplete,
  resetKey,
}) => {
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const hasCompleted = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const showProgressRef = useRef(showProgress);
  const startTimeRef = useRef(0);
  const lastProgressRef = useRef(-1);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    showProgressRef.current = showProgress;
  }, [showProgress]);

  const complete = useCallback(() => {
    if (hasCompleted.current) return;

    hasCompleted.current = true;
    setIsRunning(false);
    if (showProgressRef.current && lastProgressRef.current !== 100) {
      lastProgressRef.current = 100;
      setProgress(100);
    }
    onCompleteRef.current?.();
  }, []);

  useEffect(() => {
    hasCompleted.current = false;
    lastProgressRef.current = -1;
    setIsRunning(false);
    setProgress(0);

    if (duration <= 0) {
      setIsRunning(true);
      complete();
      setIsRunning(false);
      return undefined;
    }

    let completionTimer = 0;
    let startFrame = 0;

    startFrame = window.requestAnimationFrame((startTime) => {
      startTimeRef.current = startTime;
      setIsRunning(true);
      completionTimer = window.setTimeout(complete, duration);
    });

    return () => {
      window.cancelAnimationFrame(startFrame);
      window.clearTimeout(completionTimer);
    };
  }, [complete, duration, resetKey]);

  useEffect(() => {
    if (!showProgress || !isRunning || duration <= 0) {
      return undefined;
    }

    lastProgressRef.current = -1;
    let progressFrame = 0;

    const updateProgress = (timestamp: number) => {
      const percentage = Math.min(((timestamp - startTimeRef.current) / duration) * 100, 100);
      const roundedPercentage = Math.round(percentage);

      if (roundedPercentage !== lastProgressRef.current) {
        lastProgressRef.current = roundedPercentage;
        setProgress(roundedPercentage);
      }

      if (percentage >= 100) {
        complete();
        return;
      }

      progressFrame = window.requestAnimationFrame(updateProgress);
    };

    progressFrame = window.requestAnimationFrame(updateProgress);

    return () => window.cancelAnimationFrame(progressFrame);
  }, [complete, duration, isRunning, resetKey, showProgress]);

  return (
    <div className="progress-timer">
      {showProgress && <div className="progress-timer-percent">{progress}%</div>}

      <div className="progress-timer-track">
        <div
          className={`progress-timer-fill ${isRunning ? 'progress-timer-fill-running' : ''}`}
          style={{ transitionDuration: isRunning ? `${duration}ms` : '0ms' }}
        />
      </div>
    </div>
  );
};

export default ProgressTimer;
